import { useState } from 'react'
import {
  Box,
  type BoxProps,
  Chip,
  Container,
  Grid,
  GridProps,
  IconButton,
  Link,
  Skeleton,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material'
import { ImageOffIcon } from 'lucide-react'
import Image from 'next/image'
import NextLink from 'next/link'

import { isHappa } from '@/hooks'
import { truncateText } from '@/utils/strings'

import { WPImage, WPLink } from '@/types'

export type SearchCard = {
  pretitle: string
  title: string
  subtitle: string
  text: string
  badge: { label: string }
  chips: { label: string }[]
  image: WPImage
  link: WPLink
}

export type SearchCardWishlistProps = {
  advertisementId: number
  isInWishlist: boolean
  onToggle: (_id: number) => void
}

/**
 * SearchCardImage - Image component with error handling and placeholder
 */
type SearchCardImageProps = {
  image: WPImage
  skeleton?: boolean
}

const SearchCardImage = ({ image, skeleton }: SearchCardImageProps) => {
  const [hasError, setHasError] = useState(false)

  if (skeleton) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />
  }

  if (!image || !image.url || hasError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200'
        }}
      >
        <ImageOffIcon size={48} style={{ color: 'rgba(0, 0, 0, 0.3)' }} />
      </Box>
    )
  }

  return <Image src={image.url} alt={image.alt || ''} fill onError={() => setHasError(true)} />
}

/**
 * Card
 */
export type SearchCardProps = GridProps &
  Partial<SearchCard & { skeleton: boolean; wishlist: SearchCardWishlistProps }>

export const SearchCard = ({
  pretitle,
  title,
  subtitle,
  text,
  badge,
  chips,
  image,
  link,
  skeleton,
  wishlist,
  sx,
  ...props
}: SearchCardProps) => {
  return (
    <Grid
      sx={{
        display: 'grid',
        position: 'relative',
        boxShadow: `0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08)`,
        borderRadius: (theme) => theme.shape.borderRadius4,
        gridTemplateRows: 'auto 1fr',
        alignItems: 'start',
        overflow: 'hidden',
        ...sx
      }}
      {...props}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: 320 / 200,
          bgcolor: 'grey.100',
          overflow: 'hidden',
          '& img': {
            objectFit: 'cover'
          }
        }}
      >
        <SearchCardImage image={image!} skeleton={skeleton} />

        {badge && !skeleton ? (
          <Chip
            label={badge?.label}
            variant="highlight"
            sx={(theme) => ({
              position: 'absolute',
              top: theme.spacing(2),
              left: theme.spacing(2)
            })}
          />
        ) : null}

        {wishlist && !skeleton ? (
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              wishlist.onToggle(wishlist.advertisementId)
            }}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'background.paper'
              }
            }}
            size="medium"
          >
            <SvgIcon
              fontSize="small"
              sx={{ color: wishlist.isInWishlist ? 'error.main' : 'text.secondary' }}
            >
              <use href={wishlist.isInWishlist ? '#ico-heart-filled' : '#ico-heart'} />
            </SvgIcon>
          </IconButton>
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          alignItems: 'flex-start',
          flexDirection: 'column',
          padding: 2,
          gap: 2
        }}
      >
        {pretitle ? (
          <Typography variant="overline" color="text.primary" component="span">
            {skeleton ? <Skeleton variant="text" width="60%" /> : pretitle}
          </Typography>
        ) : null}

        {title || link ? (
          <Typography variant="h6" color="text.secondaryContrastText" component="span">
            {skeleton ? (
              <Skeleton variant="text" width="90%" />
            ) : link ? (
              <Link
                href={link.url}
                component={NextLink}
                scroll={false}
                color="text.secondaryContrastText"
                sx={{
                  textDecoration: 'none',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0
                  }
                }}
              >
                {truncateText(title ?? '', 50) || truncateText(link.title ?? '', 50) || ''}
              </Link>
            ) : (
              truncateText(title ?? '', 50) || ''
            )}
          </Typography>
        ) : null}

        {subtitle ? (
          <Typography
            variant="textLarge"
            color="primary.main"
            component="span"
            sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
          >
            {skeleton ? <Skeleton variant="text" width="30%" /> : subtitle}
          </Typography>
        ) : null}

        {text ? (
          <Typography variant="textRegular" color="text.primary" component="p">
            {skeleton ? <Skeleton variant="text" width="50%" /> : truncateText(text, 200)}
          </Typography>
        ) : null}

        {chips?.length ? (
          <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
            {chips?.map(({ label }, index) =>
              skeleton ? (
                <Skeleton
                  variant="rounded"
                  key={`chip-${index}`}
                  width="6.4rem"
                  height="3.2rem"
                  sx={{ borderRadius: 999 }}
                />
              ) : (
                <Chip key={`chip-${index}`} label={label} variant="color" />
              )
            )}
          </Stack>
        ) : null}
      </Box>
    </Grid>
  )
}

/**
 * Grid
 */
export type SearchGridProps = BoxProps &
  Partial<{
    items: SearchCard[]
  }>

const SearchGrid = ({ items, ...props }: SearchGridProps) => {
  return (
    <Box {...props}>
      <Container>
        <Grid container>
          {items?.map((item, index) => (
            <SearchCard
              key={`item-${index}`}
              size={{ xs: 4, sm: 4, md: 4, lg: 3 }}
              skeleton={false}
              {...item}
            />
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default SearchGrid
